import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { listings } from '../data/listings';
import type { Listing } from '../types';

const SITE_URL = 'https://takunastay.com';

type Props = {
  path?: string;
  listing?: Listing;
  noindex?: boolean;
  titleOverride?: string;
};

export default function SEO({ path = '/', listing, noindex, titleOverride }: Props) {
  const { t, i18n } = useTranslation();

  const title =
    titleOverride ??
    (listing
      ? t('seo.listing.title', { name: t(`listings.${listing.id}.name`) })
      : t('seo.home.title'));

  const description = listing
    ? t(`listings.${listing.id}.blurb`, { defaultValue: t('seo.home.description') })
    : t('seo.home.description');

  const url = `${SITE_URL}${path}`;
  const ogImage = `${SITE_URL}/og-image.svg`;

  const schema = listing
    ? {
        '@context': 'https://schema.org',
        '@type': 'LodgingBusiness',
        name: t(`listings.${listing.id}.name`),
        description,
        url,
        image: ogImage,
        priceRange: `ZAR ${listing.price}+ / night`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: t(`listings.${listing.id}.area`),
          addressRegion: 'Gauteng',
          addressCountry: 'ZA',
        },
        ...(listing.rating != null && listing.reviews != null
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: listing.rating,
                reviewCount: listing.reviews,
                bestRating: 5,
              },
            }
          : {}),
        sameAs: [listing.airbnbUrl],
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'LodgingBusiness',
        name: 'takuna stay',
        description: t('seo.home.description'),
        url: SITE_URL,
        image: ogImage,
        priceRange: 'ZAR 1,500 – ZAR 3,000',
        address: {
          '@type': 'PostalAddress',
          addressRegion: 'Gauteng',
          addressCountry: 'ZA',
        },
        makesOffer: listings.map((l) => ({
          '@type': 'Offer',
          name: t(`listings.${l.id}.name`),
          areaServed: t(`listings.${l.id}.area`),
          priceCurrency: 'ZAR',
          price: l.price,
          url: l.airbnbUrl,
        })),
      };

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex" />}
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="takuna stay" />
      <meta property="og:locale" content={i18n.language} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
